import { z } from "zod";

/** Single source of truth for API input/output shapes (web + mobile + api). */

// ---------- auth ----------
export const RegisterOrgInput = z.object({
  orgName: z.string().min(2).max(200),
  abn: z.string().regex(/^\d{11}$/, "ABN must be 11 digits"),
  ownerName: z.string().min(2).max(120),
  email: z.string().email(),
  password: z.string().min(10, "Minimum 10 characters"),
  state: z.enum(["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"]),
});
export type RegisterOrgInput = z.infer<typeof RegisterOrgInput>;

export const LoginInput = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof LoginInput>;

// ---------- passkeys (WebAuthn) ----------
/** The browser-generated credential blob. Shape-checking it here would only
 *  duplicate — and lag behind — the WebAuthn verifier, which is the thing that
 *  actually decides whether it is genuine. */
const WebAuthnResponse = z.record(z.unknown());

/** Omit the email for a discoverable ("usernameless") passkey sign-in. */
export const PasskeyAuthOptionsInput = z.object({
  email: z.string().email().optional(),
});
export type PasskeyAuthOptionsInput = z.infer<typeof PasskeyAuthOptionsInput>;

export const PasskeyAuthenticateInput = z.object({
  response: WebAuthnResponse,
});
export type PasskeyAuthenticateInput = z.infer<typeof PasskeyAuthenticateInput>;

export const PasskeyRegisterInput = z.object({
  /** Names the device in the revocation list — "Work iPhone", "YubiKey 5". */
  label: z.string().min(1).max(60),
  response: WebAuthnResponse,
});
export type PasskeyRegisterInput = z.infer<typeof PasskeyRegisterInput>;

// ---------- public site ----------
/** Demo request from the marketing site — filled in by someone with no account. */
export const SiteEnquiryInput = z.object({
  name: z.string().min(2).max(120),
  email: z.string().email().max(200),
  organisation: z.string().max(200).optional(),
  phone: z.string().max(32).optional(),
  participantCount: z.enum(["1-10", "11-50", "51-200", "200+"]).optional(),
  message: z.string().max(2000).optional(),
  source: z.string().max(120).optional(),
  /** Honeypot: a field a person never sees and never fills in. Bots do. */
  website: z.string().max(200).optional(),
});
export type SiteEnquiryInput = z.infer<typeof SiteEnquiryInput>;

// ---------- billing ----------
export const RateBand = z.enum([
  "weekday_day", "weekday_evening", "weekday_night",
  "saturday", "sunday", "public_holiday", "sleepover",
]);
export type RateBand = z.infer<typeof RateBand>;

/** One period of one price. Saving over an existing start date edits it. */
export const RateCardInput = z.object({
  serviceCategory: z.string().min(1),
  band: RateBand,
  /** Cents per hour, or per occurrence for a sleepover. */
  rateCents: z.number().int().positive(),
  /** Inclusive start of the period this price applies to. */
  effectiveFrom: z.string().date(),
});
export type RateCardInput = z.infer<typeof RateCardInput>;

// ---------- participants ----------
export const ParticipantStatus = z.enum(["referral", "prospective", "onboarded", "inactive"]);

export const CreateParticipantInput = z.object({
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  ndisNumber: z.string()
    .regex(/^43\d{7}$/, "NDIS number must be 9 digits starting with 43 (e.g. 430000001)")
    .optional(),
  dateOfBirth: z.string().date().optional(),
  email: z.string().email().optional(),
  phone: z.string().max(32).optional(),
  /** Signatories for service agreements, in precedence order: guardian, then
   *  nominee, then the participant's own email. Set where someone signs on the
   *  participant's behalf. */
  guardianEmail: z.string().email().optional(),
  nomineeEmail: z.string().email().optional(),
});
export type CreateParticipantInput = z.infer<typeof CreateParticipantInput>;

// ---------- rostering ----------
export const CreateShiftInput = z.object({
  /** Wall-clock local ISO, minute precision: 2026-07-20T09:00 */
  startsAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/),
  endsAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/),
  serviceCategory: z.string().min(1),
  participantIds: z.array(z.number().int().positive()).min(1),
  workerIds: z.array(z.number().int().positive()).min(1),
  isSleepover: z.boolean().default(false),
  ratioWorkers: z.number().int().min(1).max(4).default(1),
  ratioParticipants: z.number().int().min(1).max(6).default(1),
  recurrenceWeeks: z.number().int().min(1).max(12).optional(), // weekly repeats beyond the first
});
export type CreateShiftInput = z.infer<typeof CreateShiftInput>;

export const ClockInput = z.object({
  at: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/),
  lat: z.number().optional(),
  lng: z.number().optional(),
});
export type ClockInput = z.infer<typeof ClockInput>;

/** FR-ROS: a rostered worker accepting or declining their assignment. A decline
 *  carries a reason so the coordinator re-rostering the shift knows why. */
export const ShiftResponseInput = z.object({
  response: z.enum(["accept", "decline"]),
  reason: z.string().max(500).optional(),
});
export type ShiftResponseInput = z.infer<typeof ShiftResponseInput>;

/** FR-ROS: a welfare check recorded during a shift. A check that is not "ok"
 *  must say what happened — an unexplained concern is useless to an auditor. */
export const WelfareCheckInput = z.object({
  at: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/),
  status: z.enum(["ok", "concern", "escalated"]),
  participantId: z.number().int().positive().optional(),
  note: z.string().max(2000).optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
}).refine((w) => w.status === "ok" || (w.note?.trim().length ?? 0) > 0, {
  message: "A concern or escalation must record what happened",
  path: ["note"],
});
export type WelfareCheckInput = z.infer<typeof WelfareCheckInput>;

/** FR-ROS: a worklog filed against a shift. Stored as a case note so the
 *  participant's record stays the single file an auditor reads — the shift is
 *  the context, not a separate home for the note. */
export const ShiftNoteInput = z.object({
  participantId: z.number().int().positive(),
  body: z.string().min(3).max(10_000),
  isUrgent: z.boolean().default(false),
  aiAssisted: z.boolean().default(false),
});
export type ShiftNoteInput = z.infer<typeof ShiftNoteInput>;

/** FR-AI: a participant progress report generated over a reporting period.
 *  Feeds plan reviews, so the period is explicit and the source notes are counted
 *  back to the reader — a report drawn from three notes is not the same artifact
 *  as one drawn from thirty. */
export const ProgressReportInput = z.object({
  from: z.string().date(),
  to: z.string().date(),
  focus: z.string().max(500).optional(),
}).refine((r) => r.to >= r.from, { message: "to must not be before from", path: ["to"] });
export type ProgressReportInput = z.infer<typeof ProgressReportInput>;

/** FR-WFC-03: AI-assisted screening of a candidate's application against the
 *  role's stated requirements. Produces evidence for a human reviewer — never a
 *  hire/reject decision (anti-discrimination law makes automated decisions on
 *  candidates a liability, and a shortlist is a human's call). */
export const CandidateScreenInput = z.object({
  applicationText: z.string().min(50).max(20_000),
  requirements: z.string().max(2000).optional(),
});
export type CandidateScreenInput = z.infer<typeof CandidateScreenInput>;

/** FR-AI: a natural-language question the assistant answers from the caller's
 *  own tenant, using only the tools their permissions allow. */
export const AssistantAskInput = z.object({
  question: z.string().min(3).max(1000),
});
export type AssistantAskInput = z.infer<typeof AssistantAskInput>;

/** FR-COM: a chat conversation. A "participant" conversation is bound to one
 *  participant and may only contain that participant plus staff already linked
 *  to them; a "staff" conversation may not contain a participant at all. */
export const CreateConversationInput = z.object({
  kind: z.enum(["staff", "participant"]),
  participantId: z.number().int().positive().optional(),
  title: z.string().max(200).optional(),
  memberUserIds: z.array(z.number().int().positive()).min(1).max(50),
});
export type CreateConversationInput = z.infer<typeof CreateConversationInput>;

/** FR-TEN-07: a public website page, authored by a platform administrator.
 *  Slug is the public URL segment, so it is constrained rather than free text. */
export const CmsPageInput = z.object({
  slug: z.string().min(1).max(120).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be lowercase words separated by hyphens"),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(200_000),
  excerpt: z.string().max(500).optional(),
  heroImageUrl: z.string().url().max(2000).optional(),
  seoTitle: z.string().max(200).optional(),
  seoDescription: z.string().max(500).optional(),
});
export type CmsPageInput = z.infer<typeof CmsPageInput>;

/** Editing an existing page: every field optional, but at least one present. */
export const CmsPageUpdateInput = CmsPageInput.partial().refine(
  (v) => Object.keys(v).length > 0, { message: "Nothing to update" },
);
export type CmsPageUpdateInput = z.infer<typeof CmsPageUpdateInput>;

/** FR-AVL-01: a worker's recurring weekly availability (0 = Monday … 6 = Sunday). */
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export const AvailabilityWindow = z.object({
  weekday: z.number().int().min(0).max(6),
  startTime: z.string().regex(HHMM),
  endTime: z.string().regex(HHMM),
}).refine((w) => w.startTime < w.endTime, { message: "startTime must be before endTime" });

export const SetAvailabilityInput = z.object({
  windows: z.array(AvailabilityWindow).max(21),
});
export type SetAvailabilityInput = z.infer<typeof SetAvailabilityInput>;

// ---------- mobile response shapes ----------
export const WorkerShift = z.object({
  id: z.number(),
  orgId: z.number(),
  startsAt: z.string(),
  endsAt: z.string(),
  serviceCategory: z.string(),
  status: z.string(),
  isSleepover: z.boolean(),
  recurrenceRule: z.string().nullable(),
  recurrenceParentId: z.number().nullable(),
  participants: z.array(z.object({ id: z.number(), name: z.string() })),
  /** Each rostered worker with their response — this is how a coordinator spots
   *  an unanswered or declined assignment across the roster. */
  workers: z.array(z.object({
    id: z.number(),
    name: z.string(),
    assignmentStatus: z.string(),
  })),
  /** The calling worker's own response to this shift ("assigned" until they
   *  accept or decline). Null for managers, who are not rostered on it. */
  myAssignmentStatus: z.string().nullable(),
});
export type WorkerShift = z.infer<typeof WorkerShift>;
